"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import {
  CheckCircle,
  MapPin,
  Clock,
  BarChart3,
  Sun,
  Moon,
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HowItWorks } from "@/components/how-it-works";
import { ContactForm } from "@/components/contact-form";
import { SiteFooter } from "@/components/site-footer";
import { useAppContext } from "@/context/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import "maplibre-gl/dist/maplibre-gl.css";
import { CookieConsent } from "@/components/CookieConsent";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
} from "@/components/ui/sheet";
import { useRouter } from "next/navigation";

export default function Home() {
  //const { plans, theme, setTheme } = useAppContext();
  const { plans, theme, setTheme, currentUser, logout } = useAppContext();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "monthly",
  );
  const [modal, setModal] = useState<"login" | "signup" | "worker" | null>(
    null,
  );

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const closeModal = () => setModal(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const router = useRouter();

  const goToDashboard = () => {
    if (!currentUser) return;
    switch (currentUser.role) {
      case "admin":
        router.push("/master-admin");
        break;
      case "company":
        router.push("/company-admin");
        break;
      case "worker":
        router.push("/worker-portal");
        break;
      default:
        router.push("/");
    }
  };

  // useEffect(() => {
  //   if (currentUser) {
  //     goToDashboard();
  //   }
  // }, [currentUser]);

  useEffect(() => {
    const handler = () => setModal(null);
    window.addEventListener("closeAuthModal", handler);
    return () => window.removeEventListener("closeAuthModal", handler);
  }, []);

  return (
    <div
      className={`flex flex-col min-h-screen ${
        theme === "dark" ? "dark bg-gray-900 text-white" : ""
      } transition-colors duration-200`}
    >
      {/*  HEADER  */}
      <header className="border-b sticky top-0 bg-background z-50 dark:bg-gray-900 dark:border-gray-800">
        <div className="container flex h-16 items-center justify-between px-4 sm:px-6">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <MapPin className="h-6 w-6 text-emerald-600" />
            <span className="text-xl font-bold">CheckIn-Out-Pro</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex gap-6">
            <button
              onClick={() => scrollToSection("how-it-works")}
              className="text-sm font-medium hover:underline dark:text-gray-200"
            >
              How It Works
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className="text-sm font-medium hover:underline dark:text-gray-200"
            >
              Pricing
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="text-sm font-medium hover:underline dark:text-gray-200"
            >
              Contact Us
            </button>
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden md:flex gap-3 items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
            >
              {theme === "light" ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>

            <Link href="/worker-portal">
              <Button
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
              >
                Worker Portal
              </Button>
            </Link>

            {!currentUser ? (
              <>
                <Button
                  variant="outline"
                  className="dark:border-gray-700 dark:text-gray-200"
                  onClick={() => setModal("login")}
                >
                  Log In
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                  onClick={() => setModal("signup")}
                >
                  Sign Up
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                  onClick={goToDashboard}
                >
                  Dashboard
                </Button>

                <Button
                  variant="outline"
                  className="dark:border-gray-700 dark:text-gray-200"
                  onClick={logout}
                >
                  Logout
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>

              <SheetContent
                side="left"
                className="p-6 bg-white dark:bg-gray-900"
              >
                <SheetHeader>
                  <h2 className="text-lg font-semibold mb-4">Menu</h2>
                </SheetHeader>

                <nav className="flex flex-col gap-4">
                  <button
                    onClick={() => {
                      scrollToSection("how-it-works");
                      setIsMenuOpen(false);
                    }}
                    className="text-sm font-medium hover:underline text-left"
                  >
                    How It Works
                  </button>

                  <button
                    onClick={() => {
                      scrollToSection("pricing");
                      setIsMenuOpen(false);
                    }}
                    className="text-sm font-medium hover:underline text-left"
                  >
                    Pricing
                  </button>

                  <button
                    onClick={() => {
                      scrollToSection("contact");
                      setIsMenuOpen(false);
                    }}
                    className="text-sm font-medium hover:underline text-left"
                  >
                    Contact Us
                  </button>

                  <div className="mt-6 flex flex-col gap-3">
                    {!currentUser ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setModal("login");
                            setIsMenuOpen(false);
                          }}
                        >
                          Log In
                        </Button>

                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => {
                            setModal("signup");
                            setIsMenuOpen(false);
                          }}
                        >
                          Sign Up
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => {
                            goToDashboard();
                            setIsMenuOpen(false);
                          }}
                        >
                          Dashboard
                        </Button>

                        <Button
                          variant="outline"
                          onClick={() => {
                            logout();
                            setIsMenuOpen(false);
                          }}
                        >
                          Logout
                        </Button>
                      </>
                    )}

                    <Link href="/worker-portal" className="w-full">
                      <Button
                        variant="outline"
                        className="w-full border-emerald-600 text-emerald-600"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Worker Portal
                      </Button>
                    </Link>
                  </div>

                  <div className="mt-6">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        toggleTheme();
                        setIsMenuOpen(false);
                      }}
                      className="rounded-full"
                    >
                      {theme === "light" ? (
                        <Moon className="h-5 w-5" />
                      ) : (
                        <Sun className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/*  HERO  */}
      <main className="flex-1">
        <section className="w-full py-12 md:py-20 xl:py-24 bg-gradient-to-br from-white to-emerald-50 dark:from-gray-900 dark:to-gray-800 px-4 sm:px-6">
          <div className="flex flex-col items-center justify-center text-center space-y-8 w-full max-w-6xl mx-auto">
            {/* Heading */}
            <div className="space-y-4 max-w-3xl mx-auto">
              <h1 className="text-3xl sm:text-5xl xl:text-6xl font-bold tracking-tight leading-tight">
                <span className="block">On-Time Attendance &</span>
                <span className="block text-emerald-600 dark:text-emerald-400">
                  Check-In Solution
                </span>
                <span className="block">for Your On-Site Workforce</span>
              </h1>
              <p className="text-muted-foreground md:text-xl max-w-2xl mx-auto dark:text-gray-300 leading-relaxed">
                Streamline attendance, improve accountability, and optimize
                field operations with our powerful GPS tracking platform.
              </p>
              <p className="text-emerald-600 dark:text-emerald-400 font-semibold md:text-lg">
                Free trial for 7 days up to 5 users!
              </p>
            </div>

            {/* CTA Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center w-full px-4">
              <Button
                size="lg"
                onClick={() => setModal("signup")}
                className="w-full sm:w-auto text-lg px-8 py-6 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
              >
                Start Free Trial
              </Button>
            </div>

            {/* Feature Cards */}
            <div className="mt-12 w-full px-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    icon: <MapPin className="w-8 h-8 text-emerald-500" />,
                    title: "GPS Tracking",
                    desc: "Real-time location capture at check-in and check-out.",
                  },
                  {
                    icon: <Clock className="w-8 h-8 text-emerald-500" />,
                    title: "Geo-fencing",
                    desc: "Find attendance within designated work areas.",
                  },
                  {
                    icon: <BarChart3 className="w-8 h-8 text-emerald-500" />,
                    title: "Onsite Time Tracking",
                    desc: "Track check-in/check-out and work hours instantly.",
                  },
                  {
                    icon: <BarChart3 className="w-8 h-8 text-emerald-500" />,
                    title: "Real-Time Reports",
                    desc: "Instantly view team activity and work hours.",
                  },
                ].map((item, i) => (
                  <Card
                    key={i}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow rounded-xl"
                  >
                    <CardContent className="p-8 flex flex-col items-center text-center h-full">
                      <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center mb-6">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-semibold mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                        {item.desc}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How it Works */}
        <div id="how-it-works">
          <HowItWorks />
        </div>

        {/*  PRICING SECTION  */}
        <section
          id="pricing"
          className="w-full py-12 md:py-24 lg:py-32 dark:bg-gray-900 px-4 sm:px-6"
        >
          <div className="container mx-auto">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <div className="space-y-2">
                <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl dark:text-white">
                  Pricing Plans
                </h2>
                <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed dark:text-gray-300">
                  Choose the plan that best fits your business needs.
                </p>
              </div>

              {/* Billing Toggle */}
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant={billingCycle === "monthly" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setBillingCycle("monthly")}
                  className={
                    billingCycle === "monthly"
                      ? "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                      : "dark:border-gray-700 dark:text-gray-200"
                  }
                >
                  Monthly Billing
                </Button>
                <Button
                  variant={billingCycle === "annual" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setBillingCycle("annual")}
                  className={
                    billingCycle === "annual"
                      ? "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                      : "dark:border-gray-700 dark:text-gray-200"
                  }
                >
                  Annual Billing 
                </Button>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="flex flex-col lg:flex-row gap-8 pt-12 justify-center items-stretch flex-wrap">
              {["Basic", "Standard", "Professional"].map((planName, idx) => {
                const plan = plans.find((p) => p.title === planName);
                if (!plan) return null;
                const isPopular = planName === "Standard" || plan.isPopular;
                const annualPrice = plan.annualPrice || plan.monthlyPrice * 12;

                return (
                  <div
                    key={plan._id || plan.id || idx}
                    className={`flex flex-col rounded-lg border w-full sm:w-80 bg-white dark:bg-gray-800 overflow-hidden ${
                      isPopular
                        ? "border-emerald-600 dark:border-emerald-500 shadow-lg scale-105 lg:scale-110 transition-transform"
                        : "border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    <div
                      className={`p-6 ${
                        isPopular ? "bg-emerald-50 dark:bg-emerald-900/30" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="text-2xl font-bold dark:text-white">
                          {plan.title}
                        </h3>
                        {isPopular && (
                          <span className="px-2.5 py-0.5 text-xs font-semibold bg-emerald-600 dark:bg-emerald-500 text-white rounded-full">
                            Popular
                          </span>
                        )}
                      </div>

                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
                        {plan.description}
                      </p>

                      {/* Pricing */}
                      <div className="mt-4">
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold dark:text-white">
                            {billingCycle === "monthly"
                              ? `$${plan.monthlyPrice}`
                              : `$${(annualPrice / 12).toFixed(2)}`}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400">
                            /month
                          </span>
                        </div>
                        {billingCycle === "annual" && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Billed yearly:{" "}
                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                              ${annualPrice.toFixed(2)}/year
                            </span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 p-6">
                      <ul className="space-y-3">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-sm dark:text-gray-200">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-6 bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-700">
                      <Button
                        onClick={() => setModal("signup")}
                        className={`w-full ${
                          isPopular
                            ? "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500"
                            : "bg-gray-800 hover:bg-gray-900 dark:bg-gray-700"
                        }`}
                      >
                        {isPopular ? "Get Started" : "Try Now"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/*  VIDEO SECTION  */}
        <section
          id="how-it-works-video"
          className="w-full py-12 md:py-20 flex flex-col items-center bg-white dark:bg-gray-900 px-4 sm:px-6"
        >
          <h2 className="text-3xl font-bold mb-6 dark:text-white text-center">
            How it Works
          </h2>

          <div className="w-full max-w-3xl aspect-video rounded-xl overflow-hidden shadow-lg">
            <iframe
              className="w-full h-full rounded-xl"
              src="https://www.youtube.com/embed/HfiLtd3eM4s"
              title="How it Works Video"
              loading="lazy"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </section>

	{/* FAQ Section */}
<section className="w-full py-12 md:py-24 lg:py-32 bg-muted dark:bg-gray-800 px-4 sm:px-6">
  <div className="container mx-auto">
    <div className="flex flex-col items-center justify-center space-y-4 text-center">
      <h2 className="text-3xl font-bold tracking-tighter md:text-4xl dark:text-white">
        Frequently Asked Questions
      </h2>
      <p className="max-w-[900px] text-muted-foreground md:text-xl dark:text-gray-300">
        Find answers to common questions about our service.
      </p>
    </div>

    <div className="mx-auto max-w-3xl pt-12">
      <Accordion type="single" collapsible className="w-full">
        {[
          {
            q: "What is this platform used for?",
            a: "This is a smart attendance and workforce check-in & Check-out solution designed for companies managing on-site workers. Whether you run construction sites, field teams, or distributed workforces, it helps you get detailed attendance reports. This powerful tool helps you track attendance accurately, saves your valuable time, and reduces attendance issues all from a single, user friendly platform.",
          },
          {
            q: "How accurate is the GPS tracking?",
            a: "Our GPS tracking is accurate in most conditions. Accuracy may vary based on device, environmental factors, and signal strength.",
          },
          {
            q: "How does the check-in / check-out process work?",
            a: "Workers simply tap a check-in or check-out button on their device when they arrive at or leave a job site. The system records the GPS location and time automatically.",
          },
          {
            q: "How do I set up geofencing for job sites?",
            a: "Go to 'Locations' in your dashboard, click 'Add New Location', and define your geofence by address or drawing a boundary.",
          },
          {
            q: "Can I set location-based restrictions (geo-fencing)?",
            a: "Yes! You can define a specific job site radius to help ensure employees check in or out from the correct location (without blocking the check-in).",
          },
          {
            q: "How do I export timesheet data?",
            a: "Export from the 'Reports' section. You can download CSV, Excel, or PDF files and schedule automatic exports.",
          },
        ].map((faq, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="border-b dark:border-gray-700">
            <AccordionTrigger 
              className="dark:text-white text-left hover:no-underline py-4 text-base md:text-lg leading-snug"
            >
              {/* Wrapping question in a span to control line-height and alignment */}
              <span className="pr-4">{faq.q}</span>
            </AccordionTrigger>
            <AccordionContent className="dark:text-gray-300 text-sm md:text-base leading-relaxed pb-4">
              {faq.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </div>
</section>


        {/* CONTACT  */}
        <section
          id="contact"
          className="w-full py-12 md:py-24 lg:py-32 dark:bg-gray-900 px-4 sm:px-6"
        >
          <div className="container mx-auto">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl dark:text-white">
                Get in Touch
              </h2>
              <p className="max-w-[900px] text-muted-foreground md:text-xl dark:text-gray-300">
                We’d love to hear from you. Our team is always here to help.
              </p>
            </div>
            <div className="mx-auto max-w-md pt-12">
              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <CookieConsent />

      {/* AUTH MODAL */}
      <Dialog open={!!modal} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent
          className={`p-0 border-none bg-transparent ${
            modal === "worker" ? "max-w-4xl" : "max-w-md"
          }`}
        >
          <DialogTitle className="sr-only">
            {modal === "login" ? "Login" : "Sign Up"}
          </DialogTitle>

          {modal === "login" && (
            <LoginForm onSwitch={() => setModal("signup")} />
          )}
          {modal === "signup" && (
            <SignupForm onSwitch={() => setModal("login")} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
